// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — pages/Financeiros/FluxoCaixaGerencial.tsx
//
//  Fluxo de Caixa Gerencial — versão web (muito) melhorada do desktop
//  FrmFluxoCaixaSync, a tela de BI que dá a visão geral do caixa da empresa:
//  · KPIs gerenciais (Receita, Margem de Contribuição, Lucro Operacional,
//    Resultado Líquido, Pontos de Equilíbrio PEAI/PEI)
//  · Cascata do DRE de caixa + composição da despesa operacional
//  · Evolução mensal (receita × despesa × resultado)
//  · Matriz seção gerencial → conta × mês com medidas Realizado/A.V./A.H.
//    e drill-down diário por conta (o 2º pivot do desktop)
//  Fonte: razão contábil (mov_conta_contabil), classificado pelo 1º dígito
//  do código da conta (3=Receitas ... 7=Não Operacionais).
// ════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Target,
  Scale,
  ChevronDown,
  ChevronRight,
  Info,
  RefreshCcw,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { format, startOfMonth, startOfYear, subMonths } from "date-fns";
import {
  useFluxoCaixaGerencial,
  useFluxoCaixaGerencialDetalhe,
  useFluxoCaixaGerencialReprocesso,
  type ContaDetalheRef,
} from "@/features/financeiro/hooks/useFluxoCaixaGerencial";
import { usePermissoes } from "@/contexts/PermissoesContext";
import { FORMULARIOS } from "@/domain/formularios";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  FLUXO_GERENCIAL_EXPORT_ENDPOINT,
  type FluxoCaixaGerencial as FluxoGerencialData,
  type TotalizadoresFluxoCaixa,
} from "@/features/financeiro/services/fluxoCaixaGerencialService";
import { BotaoExportar } from "@/components/BotaoExportar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// ── Helpers ──────────────────────────────────────────────────────────────────

const COR_POSITIVO = "#16a34a"; // green-600
const COR_NEGATIVO = "#dc2626"; // red-600
const COR_SUBTOTAL = "#2563eb"; // blue-600

function fmt(v: number) {
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtCompacto(v: number) {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `R$ ${(v / 1_000).toFixed(0)}k`;
  return `R$ ${v.toFixed(0)}`;
}

function fmtPct(v: number) {
  return `${v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

const tooltipStyle = {
  backgroundColor: "hsl(var(--background))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
} as const;

type Medida = "valor" | "av" | "ah";

// ── KPI card ─────────────────────────────────────────────────────────────────

function KpiCard({
  titulo,
  valor,
  percentual,
  hint,
  icon: Icon,
  destaque,
}: {
  titulo: string;
  valor: number;
  percentual?: number;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  destaque?: boolean;
}) {
  const positivo = valor >= 0;
  return (
    <Card className={destaque ? (positivo ? "border-green-500/60" : "border-red-500/60") : undefined}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Icon className="h-4 w-4" />
          {titulo}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${positivo ? "text-green-600" : "text-red-600"}`}>
          {fmt(valor)}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {percentual !== undefined ? `${fmtPct(percentual)} da receita` : hint}
        </p>
      </CardContent>
    </Card>
  );
}

// ── Cascata (waterfall) do DRE de caixa ─────────────────────────────────────

function CascataDre({ t }: { t: TotalizadoresFluxoCaixa }) {
  const data = useMemo(() => {
    const passos: { name: string; inicio: number; fim: number; subtotal?: boolean }[] = [
      { name: "Receitas", inicio: 0, fim: t.receita },
      { name: "Custos Variáveis", inicio: t.receita, fim: t.margemContribuicao },
      { name: "Margem Contrib.", inicio: 0, fim: t.margemContribuicao, subtotal: true },
      { name: "Despesas Fixas", inicio: t.margemContribuicao, fim: t.loai },
      { name: "LOAI", inicio: 0, fim: t.loai, subtotal: true },
      { name: "Investimentos", inicio: t.loai, fim: t.lucroOperacional },
      { name: "Lucro Operac.", inicio: 0, fim: t.lucroOperacional, subtotal: true },
      { name: "Não Operac.", inicio: t.lucroOperacional, fim: t.resultadoLiquido },
      { name: "Result. Líquido", inicio: 0, fim: t.resultadoLiquido, subtotal: true },
    ];
    return passos.map((p) => {
      const delta = p.fim - p.inicio;
      return {
        name: p.name,
        base: Math.min(p.inicio, p.fim),
        altura: Math.abs(delta),
        valor: p.subtotal ? p.fim : delta,
        cor: p.subtotal ? COR_SUBTOTAL : delta >= 0 ? COR_POSITIVO : COR_NEGATIVO,
      };
    });
  }, [t]);

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis
          dataKey="name"
          className="text-xs"
          tick={{ fill: "hsl(var(--muted-foreground))" }}
          angle={-30}
          textAnchor="end"
          height={70}
          interval={0}
        />
        <YAxis
          className="text-xs"
          tick={{ fill: "hsl(var(--muted-foreground))" }}
          tickFormatter={fmtCompacto}
        />
        <Tooltip
          contentStyle={tooltipStyle}
          formatter={(_v: number, _n: string, item: { payload?: { valor?: number } }) => [
            fmt(item.payload?.valor ?? 0),
            "Valor",
          ]}
        />
        <Bar dataKey="base" stackId="cascata" fill="transparent" isAnimationActive={false} />
        <Bar dataKey="altura" stackId="cascata" radius={[4, 4, 0, 0]} isAnimationActive={false}>
          {data.map((d) => (
            <Cell key={d.name} fill={d.cor} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Resumo gerencial (painel de totalizadores do desktop) ───────────────────

function ResumoGerencial({ t }: { t: TotalizadoresFluxoCaixa }) {
  const linhas: {
    label: string;
    valor: number;
    pct: number;
    negrito?: boolean;
    destaque?: boolean;
  }[] = [
    { label: "Receitas", valor: t.receita, pct: t.perReceitas },
    { label: "(-) Custos Variáveis", valor: t.custosVariaveis, pct: t.perCustosVariaveis },
    { label: "(=) Margem de Contribuição", valor: t.margemContribuicao, pct: t.perMargemContrib, negrito: true },
    { label: "(-) Despesas Fixas", valor: t.despesasFixas, pct: t.perDespesasFixas },
    { label: "(=) LOAI — Lucro Operac. antes Invest.", valor: t.loai, pct: t.perLOAI, negrito: true },
    { label: "(-) Investimentos", valor: t.investimentos, pct: t.perInvestimento },
    { label: "(=) Lucro Operacional", valor: t.lucroOperacional, pct: t.perLucroOperacional, negrito: true },
    { label: "(±) Movimento Não Operacional", valor: t.movimentoNaoOperacional, pct: t.perMovimentoNaoOperacional },
    { label: "(=) Resultado Líquido", valor: t.resultadoLiquido, pct: t.perResultadoLiquido, negrito: true, destaque: true },
    { label: "Despesa Operacional Total", valor: t.despesaOperacionalTotal, pct: t.perDespesaOperacional },
    { label: "PEAI — Ponto Equilíbrio antes Invest.", valor: t.peai, pct: t.perPEAI },
    { label: "PEI — Ponto de Equilíbrio c/ Invest.", valor: t.pei, pct: t.perPEI },
  ];

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Indicador</TableHead>
          <TableHead className="text-right">Valor</TableHead>
          <TableHead className="text-right w-20">A.V.</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {linhas.map((l) => (
          <TableRow
            key={l.label}
            className={l.destaque ? (t.resultadoPositivo ? "bg-green-500/10" : "bg-red-500/10") : undefined}
          >
            <TableCell className={l.negrito ? "font-semibold" : undefined}>{l.label}</TableCell>
            <TableCell
              className={`text-right tabular-nums ${l.negrito ? "font-semibold" : ""} ${
                l.valor < 0 ? "text-red-600" : l.destaque ? "text-green-700" : ""
              }`}
            >
              {fmt(l.valor)}
            </TableCell>
            <TableCell className="text-right tabular-nums text-muted-foreground">
              {fmtPct(l.pct)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ── Matriz seção → conta × mês ───────────────────────────────────────────────

function MatrizGerencial({
  data,
  medida,
  onContaClick,
}: {
  data: FluxoGerencialData;
  medida: Medida;
  onContaClick: (conta: ContaDetalheRef) => void;
}) {
  const [fechadas, setFechadas] = useState<Record<string, boolean>>({});

  const receitaPorMes = useMemo(() => {
    const secao3 = data.secoes.find((s) => s.codigo === "3");
    return data.meses.map((_, i) => secao3?.valores[i] ?? 0);
  }, [data]);
  const receitaTotal = useMemo(() => receitaPorMes.reduce((a, b) => a + b, 0), [receitaPorMes]);

  function celula(valor: number, indiceMes: number, totalLinha: number) {
    if (medida === "av") {
      const base = receitaPorMes[indiceMes];
      return base ? fmtPct((valor / base) * 100) : "—";
    }
    if (medida === "ah") {
      return totalLinha ? fmtPct((valor / totalLinha) * 100) : "—";
    }
    return fmt(valor);
  }

  function celulaTotal(total: number) {
    if (medida === "av") return receitaTotal ? fmtPct((total / receitaTotal) * 100) : "—";
    if (medida === "ah") return fmtPct(100);
    return fmt(total);
  }

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-64 sticky left-0 bg-background z-10">Conta</TableHead>
            {data.meses.map((m) => (
              <TableHead key={m.label} className="text-right whitespace-nowrap">
                {m.label}
              </TableHead>
            ))}
            <TableHead className="text-right font-semibold whitespace-nowrap">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.secoes.map((secao) => {
            const fechada = fechadas[secao.codigo];
            return (
              <SecaoLinhas
                key={secao.codigo}
                secao={secao}
                fechada={!!fechada}
                onToggle={() =>
                  setFechadas((prev) => ({ ...prev, [secao.codigo]: !prev[secao.codigo] }))
                }
                celula={celula}
                celulaTotal={celulaTotal}
                onContaClick={onContaClick}
              />
            );
          })}
          <TableRow className="border-t-2 bg-muted/40 font-bold">
            <TableCell className="sticky left-0 bg-muted/40 z-10">Resultado do Período</TableCell>
            {data.meses.map((m, i) => {
              const valor = data.secoes.reduce((acc, s) => acc + s.valores[i], 0);
              return (
                <TableCell
                  key={m.label}
                  className={`text-right tabular-nums whitespace-nowrap ${valor < 0 ? "text-red-600" : "text-green-700"}`}
                >
                  {medida === "valor" ? fmt(valor) : celula(valor, i, data.secoes.reduce((a, s) => a + s.total, 0))}
                </TableCell>
              );
            })}
            {(() => {
              const total = data.secoes.reduce((a, s) => a + s.total, 0);
              return (
                <TableCell
                  className={`text-right tabular-nums whitespace-nowrap ${total < 0 ? "text-red-600" : "text-green-700"}`}
                >
                  {medida === "valor" ? fmt(total) : celulaTotal(total)}
                </TableCell>
              );
            })()}
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function SecaoLinhas({
  secao,
  fechada,
  onToggle,
  celula,
  celulaTotal,
  onContaClick,
}: {
  secao: FluxoGerencialData["secoes"][number];
  fechada: boolean;
  onToggle: () => void;
  celula: (valor: number, indiceMes: number, totalLinha: number) => string;
  celulaTotal: (total: number) => string;
  onContaClick: (conta: ContaDetalheRef) => void;
}) {
  return (
    <>
      <TableRow
        className="bg-muted/30 font-semibold cursor-pointer hover:bg-muted/50"
        onClick={onToggle}
      >
        <TableCell className="sticky left-0 bg-muted/30 z-10">
          <span className="flex items-center gap-1">
            {fechada ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {secao.nome}
          </span>
        </TableCell>
        {secao.valores.map((v, i) => (
          <TableCell
            key={i}
            className={`text-right tabular-nums whitespace-nowrap ${v < 0 ? "text-red-600" : ""}`}
          >
            {celula(v, i, secao.total)}
          </TableCell>
        ))}
        <TableCell
          className={`text-right tabular-nums whitespace-nowrap ${secao.total < 0 ? "text-red-600" : ""}`}
        >
          {celulaTotal(secao.total)}
        </TableCell>
      </TableRow>
      {!fechada &&
        secao.contas.map((conta) => (
          <TableRow
            key={conta.contaId}
            className="cursor-pointer hover:bg-accent/50"
            onClick={() => onContaClick({ contaId: conta.contaId, gerencial: conta.gerencial })}
            title="Clique para ver o detalhe diário"
          >
            <TableCell className="pl-9 text-muted-foreground sticky left-0 bg-background z-10">
              <span className="text-foreground">{conta.codigo}</span> {conta.descricao}
            </TableCell>
            {conta.valores.map((v, i) => (
              <TableCell
                key={i}
                className={`text-right tabular-nums whitespace-nowrap ${v < 0 ? "text-red-600" : ""}`}
              >
                {v === 0 && conta.valores.every((x) => x === 0) ? "—" : celula(v, i, conta.total)}
              </TableCell>
            ))}
            <TableCell
              className={`text-right tabular-nums whitespace-nowrap font-medium ${conta.total < 0 ? "text-red-600" : ""}`}
            >
              {celulaTotal(conta.total)}
            </TableCell>
          </TableRow>
        ))}
    </>
  );
}

// ── Drill-down diário (2º pivot do desktop) ──────────────────────────────────

function DetalheContaDialog({
  conta,
  dtInicio,
  dtFim,
  onClose,
}: {
  conta: ContaDetalheRef | null;
  dtInicio: string;
  dtFim: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useFluxoCaixaGerencialDetalhe(conta, dtInicio, dtFim);

  return (
    <Dialog open={conta != null} onOpenChange={(open) => !open && onClose()}>
      {conta != null && (
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {data ? `${data.codigo} — ${data.descricao}` : "Detalhe da conta"}
            </DialogTitle>
            <DialogDescription>
              Movimento diário no período (razão gerencial)
            </DialogDescription>
          </DialogHeader>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full" />
              ))}
            </div>
          ) : !data || data.dias.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Nenhum movimento no período.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Créditos</TableHead>
                  <TableHead className="text-right">Débitos</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="text-right">Lançs.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.dias.map((d) => (
                  <TableRow key={d.data}>
                    <TableCell>{format(new Date(d.data), "dd/MM/yyyy")}</TableCell>
                    <TableCell className="text-right tabular-nums text-green-600">
                      {fmt(d.creditos)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">
                      {fmt(d.debitos)}
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums font-medium ${d.valor < 0 ? "text-red-600" : ""}`}
                    >
                      {fmt(d.valor)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {d.lancamentos}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell colSpan={2} />
                  <TableCell
                    className={`text-right tabular-nums ${data.total < 0 ? "text-red-600" : "text-green-700"}`}
                  >
                    {fmt(data.total)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}

// ── Reprocessar período (backfill do razão gerencial — dados migrados) ──────

function ReprocessarDialog({
  dtInicio,
  dtFim,
  open,
  onClose,
}: {
  dtInicio: string;
  dtFim: string;
  open: boolean;
  onClose: () => void;
}) {
  const [contasPagar, setContasPagar] = useState(true);
  const [contasReceber, setContasReceber] = useState(true);
  const [caixa, setCaixa] = useState(true);
  const { mutate, isPending } = useFluxoCaixaGerencialReprocesso();

  const executar = () => {
    mutate(
      { dtInicio, dtFim, contasPagar, contasReceber, caixa },
      {
        onSuccess: (r) => {
          const total = r.geradasContasPagar + r.geradasContasReceber + r.geradasCaixa;
          toast.success(
            `Período reprocessado: ${total} lançamentos gerados ` +
              `(${r.geradasContasPagar} pagar · ${r.geradasContasReceber} receber · ${r.geradasCaixa} caixa)` +
              (r.linhasRemovidas ? ` — ${r.linhasRemovidas} antigos substituídos.` : "."),
          );
          onClose();
        },
        onError: () => toast.error("Falha ao reprocessar o período. Tente novamente."),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !isPending && onClose()}>
      {open && (
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reprocessar período</DialogTitle>
            <DialogDescription>
              Reconstrói o fluxo gerencial de {format(new Date(dtInicio + "T00:00:00"), "dd/MM/yyyy")} a{" "}
              {format(new Date(dtFim + "T00:00:00"), "dd/MM/yyyy")} a partir das fontes abaixo,
              classificando pela conta contábil de cada documento. Use após importar dados ou
              corrigir classificações — pode ser executado quantas vezes precisar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2">
              <Checkbox id="rp-pagar" checked={contasPagar} onCheckedChange={(v) => setContasPagar(!!v)} />
              <Label htmlFor="rp-pagar">Contas a Pagar (baixas pagas)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="rp-receber" checked={contasReceber} onCheckedChange={(v) => setContasReceber(!!v)} />
              <Label htmlFor="rp-receber">Contas a Receber (baixas recebidas)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="rp-caixa" checked={caixa} onCheckedChange={(v) => setCaixa(!!v)} />
              <Label htmlFor="rp-caixa">Caixa (lançamentos avulsos, sem vínculo com baixas)</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              Cancelar
            </Button>
            <Button onClick={executar} disabled={isPending || (!contasPagar && !contasReceber && !caixa)}>
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reprocessando...
                </>
              ) : (
                <>
                  <RefreshCcw className="h-4 w-4 mr-2" /> Reprocessar
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}

// ── Página ───────────────────────────────────────────────────────────────────

const FluxoCaixaGerencial = () => {
  const hoje = new Date();
  const [periodo, setPeriodo] = useState("6meses");
  const [inicioCustom, setInicioCustom] = useState(format(startOfMonth(hoje), "yyyy-MM-dd"));
  const [fimCustom, setFimCustom] = useState(format(hoje, "yyyy-MM-dd"));
  const [medida, setMedida] = useState<Medida>("valor");
  const [contaDetalhe, setContaDetalhe] = useState<ContaDetalheRef | null>(null);
  const [reprocessarAberto, setReprocessarAberto] = useState(false);
  const { temPermissao } = usePermissoes();
  const podeReprocessar = temPermissao(FORMULARIOS.FLUXO_CAIXA_GERENCIAL, "incluir");

  const { dtInicio, dtFim } = useMemo(() => {
    const fim = format(hoje, "yyyy-MM-dd");
    switch (periodo) {
      case "mes":
        return { dtInicio: format(startOfMonth(hoje), "yyyy-MM-dd"), dtFim: fim };
      case "3meses":
        return { dtInicio: format(startOfMonth(subMonths(hoje, 2)), "yyyy-MM-dd"), dtFim: fim };
      case "6meses":
        return { dtInicio: format(startOfMonth(subMonths(hoje, 5)), "yyyy-MM-dd"), dtFim: fim };
      case "12meses":
        return { dtInicio: format(startOfMonth(subMonths(hoje, 11)), "yyyy-MM-dd"), dtFim: fim };
      case "ano":
        return { dtInicio: format(startOfYear(hoje), "yyyy-MM-dd"), dtFim: fim };
      default:
        return { dtInicio: inicioCustom, dtFim: fimCustom };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo, inicioCustom, fimCustom]);

  const { data, isLoading } = useFluxoCaixaGerencial(dtInicio, dtFim);
  const t = data?.totalizadores;

  const composicaoDespesa = useMemo(() => {
    if (!t) return [];
    return [
      { name: "Custos Variáveis", value: Math.abs(t.custosVariaveis), cor: "hsl(var(--chart-1))" },
      { name: "Despesas Fixas", value: Math.abs(t.despesasFixas), cor: "hsl(var(--chart-2))" },
      { name: "Investimentos", value: Math.abs(t.investimentos), cor: "hsl(var(--chart-3))" },
    ].filter((x) => x.value > 0);
  }, [t]);

  const semDados = !isLoading && (!data || data.secoes.length === 0);

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho + filtros */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Fluxo de Caixa Gerencial</h1>
          <p className="text-muted-foreground mt-1">
            Visão geral do caixa por seção gerencial — receitas, custos, margem e pontos de equilíbrio
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mes">Este mês</SelectItem>
              <SelectItem value="3meses">Últimos 3 meses</SelectItem>
              <SelectItem value="6meses">Últimos 6 meses</SelectItem>
              <SelectItem value="12meses">Últimos 12 meses</SelectItem>
              <SelectItem value="ano">Este ano</SelectItem>
              <SelectItem value="personalizado">Personalizado</SelectItem>
            </SelectContent>
          </Select>
          {periodo === "personalizado" && (
            <>
              <Input
                type="date"
                value={inicioCustom}
                onChange={(e) => setInicioCustom(e.target.value)}
                className="w-40"
              />
              <Input
                type="date"
                value={fimCustom}
                onChange={(e) => setFimCustom(e.target.value)}
                className="w-40"
              />
            </>
          )}
          {podeReprocessar && (
            <Button variant="outline" size="sm" onClick={() => setReprocessarAberto(true)}>
              <RefreshCcw className="h-4 w-4 mr-2" />
              Reprocessar período
            </Button>
          )}
          <BotaoExportar
            endpoint={FLUXO_GERENCIAL_EXPORT_ENDPOINT}
            params={{ dtInicio, dtFim }}
          />
        </div>
      </div>

      <ReprocessarDialog
        dtInicio={dtInicio}
        dtFim={dtFim}
        open={reprocessarAberto}
        onClose={() => setReprocessarAberto(false)}
      />

      {isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : semDados ? (
        <Card>
          <CardContent className="py-16 text-center space-y-2">
            <Info className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="font-medium text-foreground">Nenhum movimento gerencial no período</p>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto">
              Se você acabou de importar dados (plano de contas, contas a pagar/receber, caixa),
              use o botão <strong>Reprocessar período</strong> para gerar o fluxo a partir dos
              movimentos importados. O plano de contas deve usar a convenção gerencial — códigos
              iniciados em 3 (Receitas), 4 (Custos Variáveis), 5 (Despesas Fixas), 6
              (Investimentos) e 7 (Não Operacionais).
            </p>
            {podeReprocessar && (
              <Button className="mt-2" onClick={() => setReprocessarAberto(true)}>
                <RefreshCcw className="h-4 w-4 mr-2" />
                Reprocessar período
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        data &&
        t && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <KpiCard titulo="Receitas" valor={t.receita} icon={TrendingUp} hint="Seção 3 do plano gerencial" />
              <KpiCard titulo="Margem de Contribuição" valor={t.margemContribuicao} percentual={t.perMargemContrib} icon={Scale} />
              <KpiCard titulo="Lucro Operacional" valor={t.lucroOperacional} percentual={t.perLucroOperacional} icon={Wallet} />
              <KpiCard titulo="Resultado Líquido" valor={t.resultadoLiquido} percentual={t.perResultadoLiquido} icon={t.resultadoPositivo ? TrendingUp : TrendingDown} destaque />
              <KpiCard titulo="PEAI" valor={t.peai} icon={Target} hint="Ponto de equilíbrio antes dos investimentos" />
              <KpiCard titulo="PEI" valor={t.pei} icon={Target} hint="Ponto de equilíbrio com investimentos" />
            </div>

            {/* Cascata + Resumo */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Cascata do Resultado</CardTitle>
                  <CardDescription>
                    Da receita ao resultado líquido — o DRE de caixa do período
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <CascataDre t={t} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Resumo Gerencial</CardTitle>
                  <CardDescription>
                    Totalizadores com análise vertical (% sobre a receita)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResumoGerencial t={t} />
                </CardContent>
              </Card>
            </div>

            {/* Evolução mensal + composição de despesa */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Evolução Mensal</CardTitle>
                  <CardDescription>Receita × despesa operacional × resultado líquido</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={data.resumoMensal}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="label"
                        className="text-xs"
                        tick={{ fill: "hsl(var(--muted-foreground))" }}
                      />
                      <YAxis
                        className="text-xs"
                        tick={{ fill: "hsl(var(--muted-foreground))" }}
                        tickFormatter={fmtCompacto}
                      />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmt(v)} />
                      <Legend />
                      <Bar dataKey="receita" name="Receita" fill={COR_POSITIVO} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="despesaOperacional" name="Despesa Operacional" fill={COR_NEGATIVO} radius={[4, 4, 0, 0]} />
                      <Line
                        type="monotone"
                        dataKey="resultadoLiquido"
                        name="Resultado Líquido"
                        stroke={COR_SUBTOTAL}
                        strokeWidth={2}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Despesa Operacional</CardTitle>
                  <CardDescription>Composição no período</CardDescription>
                </CardHeader>
                <CardContent>
                  {composicaoDespesa.length === 0 ? (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      Nenhuma despesa no período
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={composicaoDespesa}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={60}
                          outerRadius={95}
                          paddingAngle={2}
                          isAnimationActive={false}
                        >
                          {composicaoDespesa.map((c) => (
                            <Cell key={c.name} fill={c.cor} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmt(v)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Matriz seção → conta × mês */}
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div>
                    <CardTitle>Matriz Gerencial</CardTitle>
                    <CardDescription>
                      Seções e contas por mês — clique numa conta para o detalhe diário
                    </CardDescription>
                  </div>
                  <Tabs value={medida} onValueChange={(v) => setMedida(v as Medida)}>
                    <TabsList>
                      <TabsTrigger value="valor">Realizado</TabsTrigger>
                      <TabsTrigger value="av">A.V. %</TabsTrigger>
                      <TabsTrigger value="ah">A.H. %</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </CardHeader>
              <CardContent>
                <MatrizGerencial data={data} medida={medida} onContaClick={setContaDetalhe} />
                <p className="text-xs text-muted-foreground mt-2">
                  A.V. = participação sobre a receita do mês · A.H. = distribuição da linha ao longo
                  dos meses · Valores com sinal: débitos aparecem negativos.
                </p>
              </CardContent>
            </Card>

            <DetalheContaDialog
              conta={contaDetalhe}
              dtInicio={dtInicio}
              dtFim={dtFim}
              onClose={() => setContaDetalhe(null)}
            />
          </>
        )
      )}
    </div>
  );
};

export default FluxoCaixaGerencial;
